import { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight, Search } from "lucide-react";
import { NAV } from "../../data/site";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [q, setQ] = useState("");
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-brand-line" : "bg-brand-bg border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex h-[72px] items-center justify-between">
          <Link to="/" data-testid="logo-link" className="group flex items-center">
            <img src="/logo.webp" alt="MATRIVON — Advisory, Insights, Technical Intelligence" width="480" height="480" decoding="async" fetchpriority="high" className="h-14 w-auto lg:h-16" />
          </Link>

          <nav className="hidden items-center gap-7 lg:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative text-sm font-medium transition-colors hover:text-brand-blue ${
                    isActive ? "text-brand-blue" : "text-brand-ink"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <form onSubmit={doSearch} className="hidden items-center xl:flex">
              <div className="flex items-center border border-brand-line bg-white">
                <Search className="ml-2.5 h-4 w-4 text-brand-muted" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search…"
                  data-testid="nav-search-input"
                  className="h-9 w-36 bg-transparent px-2 text-sm text-brand-ink outline-none placeholder:text-brand-muted"
                />
              </div>
            </form>
            <Link
              to="/search"
              data-testid="nav-search-link"
              className="inline-flex h-10 w-10 items-center justify-center text-brand-ink hover:text-brand-blue xl:hidden lg:inline-flex"
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              data-testid="nav-cta-desktop"
              className="hidden items-center gap-2 bg-brand-blue px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-px hover:bg-brand-bluedark lg:inline-flex"
            >
              Discuss a Business Challenge <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex h-10 w-10 items-center justify-center border border-brand-line text-brand-ink lg:hidden"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="border-t border-brand-line bg-white lg:hidden">
          <nav className="mx-auto max-w-7xl px-5 py-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block border-b border-brand-line py-3 text-base font-medium ${
                    isActive ? "text-brand-blue" : "text-brand-ink"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-mobile"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 bg-brand-blue px-5 py-3 text-sm font-semibold text-white"
            >
              Discuss a Business Challenge <ArrowRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
